<template>
  <div>
    <label :for="`${fieldName}-type`" class="a-select-label">
      {{dateRangeDisplay}}
      <slot name="label"></slot>
    </label>
    <row>
      <column>
        <a-select
          v-model="datetimeType"
          :options="typeOptions"
          :id="`${fieldName}-type`"
          compress
          :disabled="disabled"
        ></a-select>
      </column>
      <suffix v-if="datetimeType === DatetimeType.Formula">
        <button class="btn-icon grey-to-blue formula" @click.self="editFormula" :disabled="disabled">
          <span class="florin icon"></span>
          <span class="sr-only">{{displayName}} {{modeString}} Formula Button</span>
        </button>
      </suffix>
    </row>
    <date-picker
      v-if="datetimeType === DatetimeType.Custom"
      :key="fieldName"
      :mode="datetimeMode"
      v-model="parent[fieldName]"
      :disabled="disabled || datetimeType !== DatetimeType.Custom"
      v-bind="datePickerProps"
    ></date-picker>
    <template v-else-if="datetimeType === DatetimeType.Formula">
      Formula: {{formulaText}}
    </template>

    <formula-modal
      v-if="editedFormula"
      :dynamic-options="dynamicOptions"
      :formula="editedFormula"
      :state="modalStateFor(vueModalID.formulaEditor)"
      :index="vueModalID.formulaEditor"
      @toggle-modal="hideFormulaModal"
      @saved="(eventData) => $emit('saved', eventData)"
    ></formula-modal>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import {DatetimeType} from "~/models/datetimeType";
  import {IDefaultVOption} from "~/interfaces/vSelectInterfaces";
  import {DatetimeMode} from "~/models/datetimeMode";
  import parse from "~/helpers/parse";
  import FormulaModal from "~/components/formulas/formulaModal.vue";
  import formulaMixin from "~/mixins/formulaMixin";
  import {IDynamicFieldsOptions} from "~/interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import organizationMixin from "~/mixins/organizationMixin";

  export default Vue.extend({
    components: {
      FormulaModal,
    },
    mixins: [formulaMixin, organizationMixin],
    props: {
      datetimeMode: {
        type: Number as () => DatetimeMode,
        required: true
      },
      parent: {
        type: Object,
        required: true
      },
      fieldName: {
        type: String,
        required: true
      },
      displayName: {
        type: String,
        required: true
      },
      disabled: {
        type: Boolean,
        default: false
      },
      datePickerProps: {
        type: Object,
        default: {}
      },
      customMinMaxTypeOptions: {
        type: Array as () => IDefaultVOption[]
      },
      allowFormula: {
        type: Boolean,
        default: false
      },
      dynamicOptions: {
        type: Array as () => IDynamicFieldsOptions[],
      },
    },
    data() {
      return {
        DatetimeType,
        editedFormula: null,
      };
    },
    created() {
      this.generateEditedFormula();
    },
    computed: {
      datetimeType: {
        get(): DatetimeType {
          return this.parent[this.fieldName + '_type'];
        },
        set(value: DatetimeType) {
          this.parent[this.fieldName + '_type'] = value;
        }
      },
      modeString(): string {
        switch (this.datetimeMode) {
          case DatetimeMode.Date:
            return 'Date';
          case DatetimeMode.Time:
            return 'Time';
          case DatetimeMode.Datetime:
            return 'Date and Time';
        }
      },
      dateRangeDisplay(): string {
        return `${this.displayName} ${this.modeString}`;
      },
      typeOptions(): IDefaultVOption[] {
        const options: IDefaultVOption[] = [
          {label: `No ${this.displayName}`, value: DatetimeType.None},
        ];
        if (this.customMinMaxTypeOptions) {
          options.push(...this.customMinMaxTypeOptions.map(template => {
            return {label: parse(template.label, this.modeString), value: template.value};
          }));
        }
        if (this.allowFormula) {
          options.push({label: 'Formula', value: DatetimeType.Formula});
        }
        options.push({label: `Pick a ${this.modeString}`, value: DatetimeType.Custom});
        return options;
      },
      form() {
        return this.getFieldsHandler.form;
      }
    },
    methods: {
      editFormula(): void {
        this.generateEditedFormula();
        this.showModal(this.vueModalID.formulaEditor);
      },
      hideFormulaModal(): void {
        this.hideModal(this.vueModalID.formulaEditor);
      },
    },
  });
</script>
